import React, { useState } from "react";
import { Redirect } from "react-router-dom";
import {
  Box, Paper, Typography, TextField,
  Button, Alert, CircularProgress, InputAdornment, IconButton,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import { signin, authenticate, isAutheticated } from "../auth/helper";

const Signin = () => {
  const [values, setValues] = useState({ email: "", password: "", error: "", loading: false, showPassword: false });
  const { email, password, error, loading, showPassword } = values;

  const kicked = typeof window !== "undefined" && sessionStorage.getItem("sessionKicked") === "1";
  if (kicked) sessionStorage.removeItem("sessionKicked");

  const handleChange = (field) => (e) => setValues({ ...values, error: "", [field]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setValues({ ...values, error: "", loading: true });
    signin({ email, password })
      .then((data) => {
        if (!data || data.error) {
          setValues({ ...values, error: (data && data.error) || "Sign-in failed", loading: false });
        } else {
          authenticate(data, () => setValues({ ...values, loading: false }));
        }
      })
      .catch(() => setValues({ ...values, error: "Network error", loading: false }));
  };

  if (isAutheticated()) {
    const role = isAutheticated()?.admin?.role;
    return <Redirect to={role === 1 ? "/dashboard" : "/feeds"} />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        {/* Brand mark */}
        <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "10px",
              bgcolor: "rgba(59,130,246,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <RssFeedIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.1 }}>FeedCore</Typography>
            <Typography variant="caption" color="text.secondary">RSS Ingestion Admin</Typography>
          </Box>
        </Box>

        <Typography variant="h5" sx={{ mb: 0.5 }}>Welcome back</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign in to your admin account
        </Typography>

        {kicked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You were signed out because this account logged in from another device.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={onSubmit}>
          <TextField
            fullWidth label="Email address" type="email" variant="outlined"
            value={email} onChange={handleChange("email")}
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} /></InputAdornment> }}
            sx={{ mb: 2 }} required
          />
          <TextField
            fullWidth label="Password" variant="outlined"
            type={showPassword ? "text" : "password"}
            value={password} onChange={handleChange("password")}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setValues({ ...values, showPassword: !showPassword })}>
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }} required
          />
          <Button
            type="submit" variant="contained" fullWidth size="large"
            disabled={loading}
            sx={{ py: 1.4, fontSize: "0.95rem" }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Signin;
